/**
 * MyAnalyticsPage — staff "My Analytics" dashboard
 *
 * Lean, mobile-first view of the logged-in user's own quotation stats:
 *   • This month: quotations + value
 *   • All-time: quotations + value + average + conversion
 *   • Top 10 customers (theirs only)
 *
 * Data source: GET /api/admin/my-analytics. Backend filters quotations
 * by created_for_user_id || created_by so creators and salespeople each
 * see their own numbers correctly.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { api, formatINR, useAuth } from '../lib/store';
import { TrendingUp, FileText, IndianRupee, Users, Truck, Calendar, CheckCircle2, Clock, XCircle, X } from 'lucide-react';

// Icon chip colours per tone (the Visualiser's analytics stat tiles).
const TONE = {
  olive: 'bg-heda-olive-tint text-heda-olive-dark',
  gold: 'bg-heda-gold-tint text-heda-gold',
  emerald: 'bg-emerald-50 text-emerald-700',
  amber: 'bg-amber-50 text-amber-700',
  red: 'bg-red-50 text-red-700',
};

function StatCard({ icon: Icon, label, value, sub, tone = 'olive', testId }) {
  return (
    <Card className="rounded-2xl bg-white shadow-none border-heda-line" data-testid={testId}>
      <CardContent className="pt-4 pb-3 px-3 sm:px-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider text-heda-mute truncate">{label}</p>
            <p className="font-display text-lg sm:text-2xl font-bold text-heda-ink mt-1 truncate tabular-nums">{value}</p>
            {sub && <p className="text-[10px] text-heda-mute mt-0.5 truncate">{sub}</p>}
          </div>
          <span className={`flex h-8 w-8 sm:h-9 sm:w-9 flex-shrink-0 items-center justify-center rounded-full ${TONE[tone]}`}>
            <Icon className="w-4 h-4" />
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

export default function MyAnalyticsPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Custom date-range filter — staff types two yyyy-mm-dd dates (or one
  // and leaves the other blank for open-ended). When both are blank the
  // backend returns the standard all-time + this-month numbers; when at
  // least one is set, ALL the cards + top-customer list reflect ONLY
  // quotations created inside that window. Lets staff answer "what did
  // I do from 2 Jun to 4 Jun?" or "only 2 Jun" by setting from=to=2 Jun.
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo)   p.date_to   = dateTo;
    api.getMyAnalytics(p)
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(err => { if (!cancelled) setError(err.response?.data?.detail || 'Failed to load analytics'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dateFrom, dateTo]);

  const s = data?.summary || {};

  return (
    <div className="space-y-4 sm:space-y-6 px-3 sm:px-6 py-4 sm:py-6 max-w-5xl mx-auto" data-testid="my-analytics-page">
      <header>
        <h1 className="font-display text-[20px] font-bold text-heda-ink">My Analytics</h1>
        <p className="text-xs sm:text-sm text-heda-sub mt-0.5">
          {user?.name ? `Welcome back, ${user.name}.` : 'Your personal performance.'} Here's how you're tracking.
        </p>
      </header>

      {/* Date range filter — type both dates for a window, or one for
          open-ended. Set from=to to a single day for a one-day view
          ("only 2 June quotations"). Clearing both restores the
          all-time + this-month default view. */}
      <Card className="rounded-2xl bg-white shadow-none border-heda-line" data-testid="my-analytics-date-filter">
        <CardContent className="pt-4 pb-3 px-3 sm:px-4">
          <div className="flex flex-col sm:flex-row sm:items-end gap-2 sm:gap-3">
            <div className="flex-1 min-w-0">
              <Label htmlFor="my-date-from" className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute text-[10px]">From</Label>
              <Input
                id="my-date-from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="rounded-xl bg-heda-cream/50 h-9 text-xs sm:text-sm mt-1"
                data-testid="my-analytics-date-from"
              />
            </div>
            <div className="flex-1 min-w-0">
              <Label htmlFor="my-date-to" className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute text-[10px]">To</Label>
              <Input
                id="my-date-to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="rounded-xl bg-heda-cream/50 h-9 text-xs sm:text-sm mt-1"
                data-testid="my-analytics-date-to"
              />
            </div>
            {(dateFrom || dateTo) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setDateFrom(''); setDateTo(''); }}
                className="rounded-xl h-9 border-heda-mute text-heda-sub text-xs"
                data-testid="my-analytics-clear-date"
              >
                <X className="w-3 h-3 mr-1" /> Clear
              </Button>
            )}
          </div>
          {data?.range_active && (
            <p className="text-[10px] text-heda-olive-dark mt-2" data-testid="my-analytics-range-banner">
              Showing only quotations from {dateFrom || '…'} to {dateTo || 'today'}
            </p>
          )}
        </CardContent>
      </Card>

      {loading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-20 sm:h-24" />)}
        </div>
      )}

      {!loading && error && (
        <Card className="rounded-2xl bg-white shadow-none border-red-200"><CardContent className="pt-4 text-sm text-red-700" data-testid="my-analytics-error">{error}</CardContent></Card>
      )}

      {!loading && !error && data && (
        <>
          {/* Stat row — this month + lifetime totals. 2 cols on mobile,
              4 cols from sm: up. Designed to fit comfortably on one
              phone screen with no horizontal scroll. */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard
              icon={Calendar} tone="gold"
              label={data?.range_active ? 'In Range' : 'This Month'}
              value={s.my_this_month_count || 0}
              sub={formatINR(s.my_this_month_value || 0)}
              testId="my-this-month-card"
            />
            <StatCard
              icon={FileText} tone="olive"
              label="Total Quotes"
              value={s.my_total_quotations || 0}
              sub={data?.range_active ? 'in range' : 'all-time'}
              testId="my-total-quotes-card"
            />
            <StatCard
              icon={IndianRupee} tone="amber"
              label="Total Value"
              value={formatINR(s.my_total_value || 0)}
              sub={`Avg ${formatINR(s.my_avg_value || 0)}`}
              testId="my-total-value-card"
            />
            <StatCard
              icon={CheckCircle2} tone="emerald"
              label="Conversion"
              value={`${s.my_conversion_rate || 0}%`}
              sub={`${s.my_delivered_count || 0} closed`}
              testId="my-delivered-card"
            />
          </div>

          {/* Status row — Closed / Pending / Lost counts for the staff's
              own quotations. Mirrors the buttons on /quotations cards. */}
          <div className="grid grid-cols-3 gap-3">
            <StatCard
              icon={CheckCircle2} tone="emerald"
              label="Closed"
              value={s.my_closed_count || 0}
              sub={formatINR(s.my_closed_value || 0)}
              testId="my-closed-card"
            />
            <StatCard
              icon={Clock} tone="amber"
              label="Pending"
              value={s.my_pending_count || 0}
              sub="awaiting"
              testId="my-pending-card"
            />
            <StatCard
              icon={XCircle} tone="red"
              label="Lost"
              value={s.my_lost_count || 0}
              sub={formatINR(s.my_lost_value || 0)}
              testId="my-lost-card"
            />
          </div>

          {/* Top customers list — phone friendly: stacked rows that
              wrap on small screens but read like a leaderboard. */}
          <Card className="rounded-2xl bg-white shadow-none border-heda-line">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base text-heda-ink">
                <Users className="w-4 h-4 text-heda-gold" />
                My Top Customers
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {(data.top_customers || []).length === 0 ? (
                <p className="text-sm text-heda-mute py-6 text-center" data-testid="my-no-customers">
                  No quotations attributed to you yet. Create one to see your numbers here.
                </p>
              ) : (
                <ul className="divide-y divide-heda-line" data-testid="my-top-customers-list">
                  {data.top_customers.map((c, i) => (
                    <li key={`${c.mobile || c.name}-${i}`} className="py-2.5 flex items-center gap-3">
                      <span className="w-6 text-xs font-mono-data text-heda-mute text-right flex-shrink-0">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-heda-ink truncate">{c.name}</p>
                        {c.mobile && <p className="text-[10px] text-heda-mute truncate">{c.mobile}</p>}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-mono-data font-semibold text-heda-olive-dark">{formatINR(c.value)}</p>
                        <p className="text-[10px] text-heda-mute">{c.count} {c.count === 1 ? 'quote' : 'quotes'}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {(data.top_customers || []).length > 0 && (
                <p className="text-[10px] text-heda-mute mt-3 italic flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> Showing top 10 customers from quotations attributed to you.
                </p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
